import { AppEvent } from "../Constants";
import { GameFocusEvent } from "../renderer/FocusEvent";
import { SoundManager } from "./../manager/SoundManager";
import { AbstractGameController } from "./AbstractGameController";
import { CommonGameEvent } from "./event/CommonGameEvent";
import { SoundChannelType } from "./type/SoundChannelType";
/**
 * AbstractSoundController.ts
 *
 * Uses the SoundManager to control sounds in relation to game events.
 */
export class AbstractSoundController extends AbstractGameController {
    create() {
        super.create();
        this.createSoundManager();
        this.musicMuted = false;
        this.sfxMuted = false;
    }
    /**
     * Save a reference to the SoundManager singleton.
     * @see {@link SoundManager}
     */
    createSoundManager() {
        this.soundManager = SoundManager.getInstance();
    }
    addEventListeners() {
        super.addEventListeners();
        this.registerCore(AppEvent.SOUND_ON, this.handleSoundOn);
        this.registerCore(AppEvent.SOUND_OFF, this.handleSoundOff);
        this.registerGame(CommonGameEvent.BACKGROUND_SOUND_OFF, this.handleBackgroundSoundOff);
        this.registerGame(CommonGameEvent.BACKGROUND_SOUND_ON, this.handleBackgroundSoundOn);
        this.registerGame(CommonGameEvent.SFX_ON, this.handleSFXOn);
        this.registerGame(CommonGameEvent.SFX_OFF, this.handleSFXOff);
        this.registerCore(GameFocusEvent.OUT, this.handleFocusOut);
        this.registerCore(GameFocusEvent.IN, this.handleFocusIn);
    }
    /**
     * Enable the three sound channels.
     * @see {@link SoundChannelType}
     */
    handleSoundOn() {
        this.soundManager.enableSound();
        this.soundManager.unmuteChannel(SoundChannelType.MUSIC);
        this.soundManager.unmuteChannel(SoundChannelType.EFFECTS);
        this.soundManager.unmuteChannel(SoundChannelType.AMBIENT);
    }
    /**
     * Disable the three channels.
     * @see {@link SoundChannelType}
     */
    handleSoundOff() {
        this.soundManager.disableSound();
        this.soundManager.muteChannel(SoundChannelType.MUSIC);
        this.soundManager.muteChannel(SoundChannelType.EFFECTS);
        this.soundManager.muteChannel(SoundChannelType.AMBIENT);
        this.dispatchCore(CommonGameEvent.SOUND_MUTED);
    }
    /** Music */
    handleBackgroundSoundOn() {
        this.soundManager.unmuteChannel(SoundChannelType.MUSIC);
        this.musicMuted = false;
    }
    /** Music */
    handleBackgroundSoundOff() {
        this.soundManager.muteChannel(SoundChannelType.MUSIC);
        this.musicMuted = true;
        if (this.sfxMuted) {
            this.dispatchCore(CommonGameEvent.SOUND_MUTED);
        }
    }
    /** Effects, Ambient */
    handleSFXOn() {
        this.soundManager.unmuteChannel(SoundChannelType.EFFECTS);
        this.soundManager.unmuteChannel(SoundChannelType.AMBIENT);
        this.sfxMuted = false;
    }
    /** Effects, Ambient */
    handleSFXOff() {
        this.soundManager.muteChannel(SoundChannelType.EFFECTS);
        this.soundManager.muteChannel(SoundChannelType.AMBIENT);
        this.sfxMuted = true;
        if (this.musicMuted) {
            this.dispatchCore(CommonGameEvent.SOUND_MUTED);
        }
    }
    /**
     * When the game is paused mute all sound.
     */
    handleFocusOut() {
        this.soundManager.muteChannel(SoundChannelType.MUSIC);
        this.soundManager.muteChannel(SoundChannelType.EFFECTS);
        this.soundManager.muteChannel(SoundChannelType.AMBIENT);
    }
    /**
     * When the game is resumed unmute sounds that weren't manually muted.
     */
    handleFocusIn() {
        if (!this.musicMuted) {
            this.soundManager.unmuteChannel(SoundChannelType.MUSIC);
        }
        if (!this.sfxMuted) {
            this.soundManager.unmuteChannel(SoundChannelType.EFFECTS);
            this.soundManager.unmuteChannel(SoundChannelType.AMBIENT);
        }
    }
    /**
     * Try to play a sound. Fails silently.
     * @param properties - Sound to be played.
     * @returns Playing sound instance. Useless without the hidden Howl id.
     */
    playSound(properties) {
        if (properties) {
            return this.soundManager.play(properties);
        }
    }
    /**
     * Stop playing a sound. Fails silently.
     * @param properties - Sound to be stopped.
     */
    stopSound(properties) {
        if (properties) {
            this.soundManager.stop(properties);
        }
    }
}
//# sourceMappingURL=AbstractSoundController.js.map